import { StudentIdentifiers } from './../model/student-identifiers.model';
import { PreferredInfo } from './../model/preferred-info.model';
import { PostalAddresses } from './../../party/model/postal-addresses.model';
import { Student } from './../../../model/student/student.model';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, switchMap, of, catchError, throwError } from 'rxjs';
import { BaseRestApiService } from 'src/app/service/baseRestApiService.service';
import { UrlProviderService, EndPoints } from 'src/app/service/urlprovider.service';

@Injectable({
    providedIn: 'root'
})
export class StudentService extends BaseRestApiService {
    constructor(http: HttpClient, urlProvider: UrlProviderService) {
        super(http, urlProvider);
    }

    show(studentId: number): Observable<any> {
        return this.post<any>(EndPoints.student_Show, false, null, {
            id: studentId
        }).pipe(
            switchMap((result) => {
                const properties = Object.keys(result);
                properties.forEach((property) => {
                    if (['student'].indexOf(property) >= 0) {
                        result[property] = JSON.parse(result[property]);
                    }
                });

                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    getStudentBirthAddress(studentId: number): Observable<any> {
        return this.get<any>(EndPoints.student_GetStudentBirthAddress, '', studentId);
    }

    getPreferredInfo(studentId: number): Observable<any> {
        return this.get<any>(EndPoints.student_GetPreferredInfo, '', studentId);
    }

    getParents(studentId: number): Observable<any> {
        return this.get<any>(EndPoints.student_GetParents, '', studentId);
    }

    getIdentifiers(studentId: number): Observable<any> {
        return this.get<any>(EndPoints.student_GetIdentifiers, '', studentId);
    }

    update(editStudent: Student): Observable<any> {
        return this.post<any>(EndPoints.student_Update, false, null, {
            editStudent: editStudent
        }).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    addBirthAddress(birthAddress: PostalAddresses, partyID: number): Observable<any> {
        return this.post<any>(EndPoints.student_AddBirthAddress, false, null, {
            birthAddress: birthAddress,
            partyID: partyID
        }).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    updateBirthAddress(birthAddress: PostalAddresses): Observable<any> {
        return this.post<any>(EndPoints.student_UpdateBirthAddress, false, null, {
            birthAddress: birthAddress
        }).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    updateParents(editParent: any): Observable<any> {
        return this.post<any>(EndPoints.student_updateParents, false, null, {
            editParent: editParent
        }).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    createStudentIdentifier(studentID: number, IdentifierTypeId: number, IdentifierValue: string, orgID?: number): Observable<any> {
        return this.post<any>(EndPoints.student_CreateIDNew, false, null, {
            studentID: studentID,
            IdentifierTypeId: IdentifierTypeId,
            IdentifierValue: IdentifierValue,
            orgID: orgID
        }).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    deleteStudentIdentifier(studentIdentifierID: number): Observable<any> {
        return this.post<any>(EndPoints.student_DeleteStudentIdentifier, false, null, {
            studentIdentifierID: studentIdentifierID
        }).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    updateStudentIdentifier(editStudent: Student, studentIdentifier: any): Observable<any> {
        return this.post<any>(EndPoints.student_UpdateStudentIdentifier, false, null, {
            editStudent: editStudent,
            studentIdentifier: studentIdentifier
        }).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    editPreferredInfo(studentId: number, preferredInfo: PreferredInfo): Observable<any> {
        return this.post<any>(EndPoints.student_EditPreferredInfo, false, null, {
            studentId: studentId,
            preferredInfo: preferredInfo
        }).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    checkExistingStudentSSID(studentID: number, ssid: string): Observable<any> {
        return this.post<any>(EndPoints.student_CheckExistingStudentSSID, false, null, {
            studentID: studentID,
            ssid: ssid
        }).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    updateStudentContactInformation(requestPayload: any): Observable<any> {
        return this.post<any>(EndPoints.student_UpdateStudentContactInformation, false, '', requestPayload).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    getCoesDetailed(studentId: number): Observable<any> {
        return this.get<any>(EndPoints.student_GetCoesDetailed, '', studentId);
    }

    verifyCoe(studentId: number, coeId: number): Observable<any> {
        return this.post<any>(EndPoints.student_VerifyCoe, false, '', {
            studentID: studentId,
            coeID: coeId
        }).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    getCoeRelatedStudents(requestPayload: any): Observable<any> {
        return this.post<any>(EndPoints.student_GetCoeRelatedStudents, false, '', requestPayload).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    getCoeRelatedStudentsByContactMechanism(requestPayload: any): Observable<any> {
        return this.post<any>(EndPoints.student_GetCoeRelatedStudentsByContactMechanism, false, '', requestPayload).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    simpleSearchSeparateBirthCity(requestPayload: any): Observable<any> {
        return this.post<any>(EndPoints.student_SimpleSearchSeparateBirthCity, false, '', requestPayload).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }
}
